import { useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useAuthStore } from '@/stores/auth-store';
import { usePermissions } from '@/hooks/usePermissions';
import { useDepartments } from '@/api/queries/useDepartments';

/** Routes that carry the managed-department `dept` search param. */
type DepartmentTabRoute = '/$slug/department/members' | '/$slug/department/teams';

/**
 * Shared selection state for the Meine Abteilung tabs: which departments the
 * user manages, which one is active (the URL `dept` param when it names a
 * managed department, the first managed department otherwise), and the URL
 * sync for switching departments / redirecting away from an invalid param.
 *
 * The caller reads the `dept` search param via its route's `useSearch` (the
 * route id literal can't be derived from `route` type-safely) and passes it in.
 */
export function useManagedDepartmentSelection(
  route: DepartmentTabRoute,
  selectedDeptFromUrl: string | undefined
) {
  const slug = useAuthStore().slug();
  const navigate = useNavigate();
  const { authContext } = usePermissions();
  const managedDeptIds = useMemo(
    () => authContext?.managedDeptIds ?? [],
    [authContext?.managedDeptIds]
  );
  const { data: allDepartments } = useDepartments();
  const managedDepartments = useMemo(
    () => allDepartments?.filter((d) => managedDeptIds.includes(d._id)) ?? [],
    [allDepartments, managedDeptIds]
  );
  const defaultDepartmentId = managedDeptIds[0];

  const selectedDepartmentId = useMemo(() => {
    if (selectedDeptFromUrl && managedDepartments.some((d) => d._id === selectedDeptFromUrl)) {
      return selectedDeptFromUrl;
    }
    return defaultDepartmentId;
  }, [selectedDeptFromUrl, managedDepartments, defaultDepartmentId]);

  const selectedDepartment = useMemo(
    () => managedDepartments.find((d) => d._id === selectedDepartmentId),
    [managedDepartments, selectedDepartmentId]
  );

  const handleDepartmentChange = useCallback(
    (departmentId: string) => {
      navigate({ to: route, params: { slug }, search: { dept: departmentId } });
    },
    [navigate, route, slug]
  );

  useEffect(
    function redirectToValidDepartment() {
      if (selectedDeptFromUrl && selectedDepartmentId !== selectedDeptFromUrl) {
        navigate({
          to: route,
          params: { slug },
          search: selectedDepartmentId ? { dept: selectedDepartmentId } : {},
          replace: true
        });
      }
    },
    [selectedDeptFromUrl, selectedDepartmentId, navigate, route, slug]
  );

  return {
    managedDepartments,
    defaultDepartmentId,
    selectedDepartmentId,
    selectedDepartment,
    handleDepartmentChange
  };
}
